import * as React from 'react';
import { X, Plus, Users, Shield } from 'lucide-react';
import type { ProjectDto } from '@cutlyft/zod-schemas';
import { SHAREABLE_ROLE_CODES } from '@cutlyft/zod-schemas';
import { useShareProjectRole } from '@/hooks/projects/use-share-project-role';
import { useUnshareProjectRole } from '@/hooks/projects/use-unshare-project-role';
import { useShareProjectUser } from '@/hooks/projects/use-share-project-user';
import { useUnshareProjectUser } from '@/hooks/projects/use-unshare-project-user';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';

const ROLE_OPTIONS = SHAREABLE_ROLE_CODES.map((code) => ({ value: code, label: code }));

interface ProjectSharePanelProps {
  project: ProjectDto;
}

export function ProjectSharePanel({ project }: ProjectSharePanelProps) {
  const { toast } = useToast();
  const [newRoleCode, setNewRoleCode] = React.useState<string | undefined>();
  const [newUserId, setNewUserId] = React.useState('');

  const shareRole = useShareProjectRole();
  const unshareRole = useUnshareProjectRole();
  const shareUser = useShareProjectUser();
  const unshareUser = useUnshareProjectUser();

  const handleAddRole = () => {
    if (!newRoleCode) return;
    shareRole.mutate(
      { projectId: project.id, input: { roleCode: newRoleCode as typeof SHAREABLE_ROLE_CODES[number] } },
      {
        onSuccess: () => { setNewRoleCode(undefined); toast({ title: 'Role share added' }); },
        onError: () => toast({ variant: 'destructive', title: 'Failed to add role share' }),
      },
    );
  };

  const handleRemoveRole = (roleCode: string) => {
    unshareRole.mutate(
      { projectId: project.id, roleCode },
      {
        onError: () => toast({ variant: 'destructive', title: 'Failed to remove role share' }),
      },
    );
  };

  const handleAddUser = () => {
    const userId = parseInt(newUserId, 10);
    if (isNaN(userId) || userId <= 0) {
      toast({ variant: 'destructive', title: 'Enter a valid user ID' });
      return;
    }
    shareUser.mutate(
      { projectId: project.id, input: { userId } },
      {
        onSuccess: () => { setNewUserId(''); toast({ title: 'User share added' }); },
        onError: () => toast({ variant: 'destructive', title: 'Failed to add user share' }),
      },
    );
  };

  const handleRemoveUser = (userId: number) => {
    unshareUser.mutate(
      { projectId: project.id, userId },
      {
        onError: () => toast({ variant: 'destructive', title: 'Failed to remove user share' }),
      },
    );
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Role shares */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Shield className="h-4 w-4" />
            Role Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {project.roleShares.length === 0 ? (
            <p className="text-xs text-fg-muted">No roles have access yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {project.roleShares.map((rs) => (
                <Badge key={rs.id} variant="secondary" className="gap-1 pr-1">
                  {rs.roleCode}
                  <button
                    type="button"
                    onClick={() => handleRemoveRole(rs.roleCode)}
                    className="ml-0.5 rounded-sm opacity-70 hover:opacity-100"
                    aria-label={`Remove ${rs.roleCode} access`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1">
              <Combobox
                options={ROLE_OPTIONS}
                value={newRoleCode}
                onValueChange={setNewRoleCode}
                placeholder="Select role…"
                searchPlaceholder="Search role..."
                className="h-8 text-xs"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddRole}
              disabled={!newRoleCode || shareRole.isPending}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User shares */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4" />
            User Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {project.userShares.length === 0 ? (
            <p className="text-xs text-fg-muted">No individual users have access yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {project.userShares.map((us) => (
                <Badge key={us.id} variant="secondary" className="gap-1 pr-1">
                  User #{us.userId}
                  <button
                    type="button"
                    onClick={() => handleRemoveUser(us.userId)}
                    className="ml-0.5 rounded-sm opacity-70 hover:opacity-100"
                    aria-label={`Remove user ${us.userId}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              className="h-8 text-xs flex-1"
              type="number"
              placeholder="User ID"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddUser()}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddUser}
              disabled={!newUserId || shareUser.isPending}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
